import { useCallback, useEffect,useMemo, useState } from "react";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";
import { AutocompleteTags } from "@components_v2/autocomplete";
import { useTranslation } from "react-i18next";
import i18n from "@lib/i18n";
import useHashtagSuggestions from "@hooks/useHashtagSuggestions";

/**
 * 해시태그 입력. 입력값으로 프로젝트의 기존 해시태그를 자동완성 추천한다.
 * 해시태그가 아닌 값(확장자 등)은 추천이 없는 TagsInputNoStartIcon 을 쓴다.
 *
 * @param {object} props
 * @param {import("react").ElementType} [props.popperComponent] 추천 목록 popper. 생략하면 MUI 기본 Popper(body portal).
 *   필터 popper 안에 추천을 그려야 하는 검색 필터에서만 넘긴다.
 */
const TagsInput = ({
    tags,
    label,
    disabled = false,
    inputDisabled = false,
    onDeleteTag,
    onAddTag,
    isEdit,
    maxCount,
    isClear,
    popperComponent,
}) => {
    const { t } = useTranslation();
    const [value, setValue] = useState("");
    const [validationError, setValidationError] = useState(null);
    const MAX_COUNT = maxCount || 100;

    const { suggestions, fetchNext } = useHashtagSuggestions({
        enabled: !disabled && !inputDisabled && (tags?.length ?? 0) < MAX_COUNT,
        keyword: value,
        excludedTags: tags?.map((tag) => tag.value),
    });

    const validate = useCallback(
        (newValue, currentTags = tags) => {
            if (!newValue) return null;
            if (currentTags?.length >= MAX_COUNT)
                return {
                    level: "error",
                    message: i18n.t(`common::msg::The maximum number of tags, {{maxCount}}, has been exceeded.`, {
                        maxCount: MAX_COUNT,
                    }),
                };
            return validateInput(newValue, {
                maxLength: LIMIT_CHAR.tag,
                uniqueInTrimmed: currentTags?.map((tag) => tag.value) || [],
            });
        },
        [tags, MAX_COUNT],
    );

    const onHandleDeleteTag = useCallback(
        (index) => {
            const updatedTags = tags.filter((_, i) => i !== index);
            setValidationError(validate(value, updatedTags));
            onDeleteTag(index);
        },
        [onDeleteTag, tags, value, validate],
    );

    const handleSetValue = useCallback(
        (newValue) => {
            setValue(newValue);
            setValidationError(validate(newValue));
        },
        [validate],
    );

    const onHandleSubmitTag = useCallback(() => {
        // 태그는 앞뒤 공백만 제거해 등록한다 — 내부 공백은 값의 일부로 보존한다. 공백만 입력한 값은 등록하지 않는다.
        const trimmedValue = value.trim();
        if (!trimmedValue || validationError) return;
        onAddTag({ value: trimmedValue });
        setValue("");
        setValidationError(null);
    }, [value, onAddTag, validationError]);

    const onHandleSelectSuggestion = useCallback(
        (selectedTag) => {
            // 추천 태그도 직접 입력과 같은 검증(개수·길이·중복)을 거친다.
            // 값은 trim 하지 않는다 — 이미 리소스에 달린 태그를 고르는 것이므로 서버 표기를 그대로 등록한다.
            const error = validate(selectedTag);
            if (error) {
                setValue(selectedTag);
                setValidationError(error);
                return;
            }
            onAddTag({ value: selectedTag });
            setValue("");
            setValidationError(null);
        },
        [validate, onAddTag],
    );

    useEffect(() => {
        if (isClear) {
            setValue("");
            setValidationError(null);
        }
    }, [isClear]);

    return (
        <AutocompleteTags
            type={"tag"}
            label={label}
            value={value}
            placeholder={t(`common::msg::Enter after typing`)}
            tags={tags}
            disabled={disabled}
            inputDisabled={inputDisabled}
            setValue={handleSetValue}
            onDelete={onHandleDeleteTag}
            onSubmit={onHandleSubmitTag}
            isEdit={isEdit}
            maxCount={MAX_COUNT}
            helperText={validationError?.message || ""}
            error={validationError?.level === "error"}
            suggestions={suggestions}
            onFetchNextSuggestions={fetchNext}
            onSelectSuggestion={onHandleSelectSuggestion}
            popperComponent={popperComponent}
        />
    );
};

export default TagsInput;
